/**
 * Cross-target API client for the Go service.
 *
 * The Go backend is the primary API during the GCP migration. The admin SPA
 * (and, later, web/) talk to it through this client so the base URL lives in
 * exactly one configurable place.
 *
 * Base URL resolution (first match wins):
 *   1. `process.env.API_BASE_URL` — injected at build time via Bun's `define`
 *      (see admin/build.ts). Lets staging/prod builds differ without code edits.
 *   2. Fallback constant below — the product API URL, used for local
 *      `bun run dev` where no define is applied.
 *
 * The product demo target is `https://api.hireos.co.nz`; set
 * `API_BASE_URL` in a build only when pointing at a different backend.
 *
 * Admin builds can install an auth token provider with
 * `setAuthTokenProvider`; when present, every request carries
 * `Authorization: Bearer <token>`. Public web builds leave it unset.
 */

/**
 * API origin. Production builds replace the `__API_BASE__` token via Bun's
 * `define` (web/build.ts, admin/build.ts). The dev server (`bun ./index.html`)
 * applies no define, so the token is genuinely undefined there — the `typeof`
 * guard handles that and falls back to the public api hostname.
 *
 * Do NOT gate this on `typeof process`: Bun's production browser bundle has
 * no `process` global, so such a guard short-circuits to false and the
 * build-time define is never read — every request then hits the dev
 * fallback. That bug pointed the whole SPA at the raw run.app URL.
 */
declare const __API_BASE__: string | undefined;

const DEFAULT_API_BASE_URL = "https://api.hireos.co.nz";
const TENANT_HEADER = "X-HireOS-Tenant";

export const API_BASE_URL: string =
  (typeof __API_BASE__ !== "undefined" && __API_BASE__) || DEFAULT_API_BASE_URL;

type AuthTokenProvider = () => Promise<string | null> | string | null;

let authTokenProvider: AuthTokenProvider | null = null;

export function setAuthTokenProvider(provider: AuthTokenProvider | null) {
  authTokenProvider = provider;
}

export function tenantSlugFromHost(hostname: string): string | null {
  const host = hostname.trim().toLowerCase().replace(/\.$/, "");
  if (!host || host === "localhost" || host === "127.0.0.1" || host === "::1") {
    return null;
  }
  let slug: string;
  if (host.endsWith(".localhost")) {
    slug = host.slice(0, -".localhost".length);
  } else if (host.endsWith(".hireos.co.nz")) {
    slug = host.slice(0, -".hireos.co.nz".length);
  } else {
    return null;
  }
  if (slug.includes(".")) return null;
  if (!slug || slug === "www" || slug === "admin" || slug === "api") return null;
  return /^[a-z0-9][a-z0-9-]*$/.test(slug) ? slug : null;
}

/**
 * Dev-only tenant override (localStorage). Read ONLY on a bare localhost
 * host — a `{slug}.localhost` or production subdomain always wins, and the
 * Go side additionally ignores the X-HireOS-Tenant header off local dev
 * hosts, so a stale stored value can never leak into production behaviour.
 */
export const DEV_TENANT_STORAGE_KEY = "hireos.devTenant";

function isBareLocalhost(hostname: string): boolean {
  const host = hostname.trim().toLowerCase();
  return host === "localhost" || host === "127.0.0.1" || host === "::1";
}

function devTenantOverride(): string | null {
  try {
    const stored = globalThis.localStorage?.getItem(DEV_TENANT_STORAGE_KEY);
    return stored && /^[a-z0-9][a-z0-9-]*$/.test(stored) ? stored : null;
  } catch {
    return null;
  }
}

/**
 * Dev-only inquiry-profile override (localStorage): a profile route_slug the
 * inquiry form loads instead of the tenant's default profile. Same bare-
 * localhost gate as the tenant override — a production origin never reads it,
 * and it only picks a profile that already belongs to the resolved tenant, so
 * it can never cross a tenant boundary. Used by the public inquiry form to flip
 * disciplines live on the demo Company.
 */
export const DEV_PROFILE_STORAGE_KEY = "hireos.devProfile";

export function devProfileOverride(): string | null {
  if (typeof globalThis.location === "undefined") return null;
  if (!isBareLocalhost(globalThis.location.hostname)) return null;
  try {
    const stored = globalThis.localStorage?.getItem(DEV_PROFILE_STORAGE_KEY);
    return stored && /^[a-z0-9][a-z0-9-]*$/.test(stored) ? stored : null;
  } catch {
    return null;
  }
}

function currentTenantSlug(): string | null {
  if (typeof globalThis.location === "undefined") return null;
  const hostname = globalThis.location.hostname;
  if (isBareLocalhost(hostname)) return devTenantOverride();
  return tenantSlugFromHost(hostname);
}

/** Thrown when a response is non-2xx. `status` and the server `error` message
 *  (when the body is JSON with an `error` field) are preserved for callers. */
export class ApiError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

/**
 * Pull the server's own explanation out of a failed response.
 *
 * go-service emits two error shapes, both deliberate: `writeJSONError` sends
 * `{error}`, while the handlers that also report `success:false` send
 * `{message}` (send-to-client, notify-contractors, contractors, and the
 * contractor-respond redirects). Reading only `error` silently discarded the
 * second half — a Send Quote blocked by an unpriced scope line answers 400
 * with the exact line to price, and the operator saw "Request failed (400)".
 *
 * Both keys are read, `error` first. Anything else — an empty body, HTML from
 * a proxy, a non-object — falls back to the status line, which is all we
 * honestly know.
 */
async function readErrorMessage(res: Response): Promise<string> {
  const fallback = `Request failed (${res.status})`;
  try {
    const body: unknown = await res.json();
    if (body == null || typeof body !== "object") return fallback;
    const { error, message } = body as { error?: unknown; message?: unknown };
    if (typeof error === "string" && error.trim() !== "") return error;
    if (typeof message === "string" && message.trim() !== "") return message;
    return fallback;
  } catch {
    return fallback;
  }
}

interface RequestOptions {
  method?: string;
  /** JSON-serialisable body (sets the Content-Type header automatically), or
   *  a FormData for multipart requests (passed through untouched — the
   *  browser sets the multipart boundary header itself). */
  body?: unknown;
  /** Query string params. Skipped when empty. */
  query?: Record<string, string | undefined>;
  signal?: AbortSignal;
}

/** Transient gateway statuses worth retrying on idempotent GETs. The GFE
 *  occasionally returns these without a usable backend response (observed: a
 *  502 while go-service logged a clean 200) — a response the GFE severed, not
 *  one the backend failed.
 *
 *  The root cause is still unknown. Two attributions have been ruled out, so
 *  don't re-derive them: it is not IAP (the original note here blamed it, but
 *  it is `enabled = false` on both api backends and was never in the path),
 *  and it is not a load-balancer backend timeout (`timeout_sec` is unsupported
 *  for Serverless NEGs — GCP rejects it outright, and the 30s the API reports
 *  is an inert default; see infra/api-lb-iap.tf). The live request ceiling is
 *  the Cloud Run service timeout, which sits above the Go router's budget.
 *
 *  Only GETs are retried — never mutations, which are not safe to repeat (a
 *  retried POST would re-send a jobsheet / double-email). */
const RETRYABLE_STATUSES = new Set([502, 503, 504]);
const MAX_GET_ATTEMPTS = 3;

const delay = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/**
 * Fetch a binary response — the same base URL, bearer token and error shape as
 * {@link apiFetch}, but the body comes back as a Blob and the server's
 * `Content-Disposition` filename comes with it.
 *
 * The company data export (#374) is the only caller: a zip cannot be fetched
 * through `apiFetch` (which parses JSON), and it cannot be a plain link either,
 * because `/api/admin/*` needs the `Authorization` header a navigation would
 * not send. No retry — a large download that half-arrived is not something to
 * silently repeat.
 *
 * @throws {ApiError} on any non-2xx response.
 */
export async function apiFetchBlob(
  path: string,
  opts: { query?: Record<string, string | undefined>; signal?: AbortSignal } = {},
): Promise<{ blob: Blob; filename: string }> {
  const url = new URL(path, API_BASE_URL);
  if (opts.query) {
    for (const [key, value] of Object.entries(opts.query)) {
      if (value !== undefined) url.searchParams.set(key, value);
    }
  }
  const headers = new Headers();
  const token = authTokenProvider ? await authTokenProvider() : null;
  if (token) headers.set("Authorization", `Bearer ${token}`);

  const res = await fetch(url.toString(), {
    method: "GET",
    headers,
    credentials: "include",
    signal: opts.signal,
  });
  if (!res.ok) throw new ApiError(res.status, await readErrorMessage(res));

  return {
    blob: await res.blob(),
    filename: filenameFromDisposition(res.headers.get("Content-Disposition")),
  };
}

/** Pull `filename="…"` out of a Content-Disposition header; "" when absent. */
function filenameFromDisposition(header: string | null): string {
  if (!header) return "";
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header);
  return match ? decodeURIComponent(match[1]) : "";
}

/**
 * Issue a request to the Go service and parse the JSON response.
 *
 * Idempotent GETs retry up to {@link MAX_GET_ATTEMPTS} times on transient
 * gateway failures (502/503/504 or a network error) with a short backoff.
 * Non-GET requests are never retried.
 *
 * @throws {ApiError} on any non-2xx response (after retries are exhausted).
 */
export async function apiFetch<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const url = new URL(path, API_BASE_URL);
  if (opts.query) {
    for (const [key, value] of Object.entries(opts.query)) {
      if (value !== undefined) url.searchParams.set(key, value);
    }
  }

  const method = (opts.method ?? "GET").toUpperCase();
  const headers = new Headers();
  if (!(opts.body instanceof FormData) && opts.body !== undefined) {
    headers.set("Content-Type", "application/json");
  }
  const token = authTokenProvider ? await authTokenProvider() : null;
  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }
  const tenantSlug = !path.startsWith("/api/admin/") ? currentTenantSlug() : null;
  if (tenantSlug) {
    headers.set(TENANT_HEADER, tenantSlug);
  }
  const init: RequestInit = {
    method,
    headers,
    // Keep cookies for public token endpoints and same-origin compatibility.
    credentials: "include",
    signal: opts.signal,
  };
  if (opts.body instanceof FormData) {
    // No explicit Content-Type: the browser must set the multipart boundary.
    init.body = opts.body;
  } else if (opts.body !== undefined) {
    init.body = JSON.stringify(opts.body);
  }

  const maxAttempts = method === "GET" ? MAX_GET_ATTEMPTS : 1;
  for (let attempt = 1; ; attempt++) {
    const canRetry = attempt < maxAttempts && !opts.signal?.aborted;

    let res: Response;
    try {
      res = await fetch(url.toString(), init);
    } catch (err) {
      // Network-level failure. Retry idempotent GETs, otherwise propagate.
      if (canRetry) {
        await delay(300 * attempt);
        continue;
      }
      throw err;
    }

    if (!res.ok) {
      if (canRetry && RETRYABLE_STATUSES.has(res.status)) {
        await delay(300 * attempt);
        continue;
      }
      throw new ApiError(res.status, await readErrorMessage(res));
    }

    // 204 / empty bodies parse to undefined; callers that expect data won't
    // hit this path.
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }
}
